import { useEffect, useMemo, useState } from "react";

import { ChevronDownIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useColorModeValue,
  VStack
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";

import CurrentSupplements from "~/components/members/CurrentSupplements";
import MemberCard from "~/components/members/MemberCard";
import MemberRecommendations from "~/components/members/MemberRecommendations";
import RecentSupplements from "~/components/members/RecentSupplements";
import { useAppState } from "~/hooks/useAppState";
import { getMemberSupplementsQueryKey, useMemberSupplements } from "~/hooks/useMemberSupplements";
import { useUserHome } from "~/hooks/useUserHome";
import Meta from "~/layout/Meta";
import { Member, useAppStore } from "~/store";
import { updateCurrentGroupMember } from "~/utils/memberHelpers";
import { withRenderTally } from "~/utils/withRenderTally";

const MembersPageComponent = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const currentMember = useAppStore((state) => state.currentMember);
  const { editMode, debugMode } = useAppState("editMode", "debugMode");
  const { userHomeData } = useAppState("userHomeData");
  const { groupMembers } = useUserHome();
  const setCurrentMember = useAppStore((state) => state.setCurrentMember);
  const currentGroupMember = useAppStore((state) => state.currentGroupMember);
  const setCurrentGroupMember = useAppStore((state) => state.setCurrentGroupMember);
  const setEditMode = useAppStore((state) => state.setEditMode);
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const textColor = useColorModeValue("gray.700", "gray.200");

  // Use the consolidated supplements data hook with the queryClient for manual invalidation
  const queryClient = useQueryClient();
  const {
    data: memberSupplements,
    isLoading: isLoadingSupplements,
    refetch: refetchSupplements
  } = useMemberSupplements(currentGroupMember?.id);

  // Determine if this is a new member (id === 0) or an existing one
  const isNewMember = currentMember?.id === 0;

  // Extract the members from userHomeData.groupMembers
  const members = useMemo(() => {
    if (!groupMembers) return [];
    return groupMembers.map((gm) => gm.member);
  }, [groupMembers]);

  // Create page title based on current state - more subtle approach for edit mode
  const pageTitle = !currentMember ? "Members" : isNewMember ? "Add New Member" : currentMember.name;

  // Set up event listener for supplement changes
  useEffect(() => {
    const handleSupplementChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log("Member supplements changed event received:", customEvent.detail);

      // Invalidate the supplements query to force a refetch
      queryClient.invalidateQueries({
        queryKey: getMemberSupplementsQueryKey(currentGroupMember?.id)
      });

      // Refetch the supplements data
      refetchSupplements();
    };

    // Add event listener
    window.addEventListener("member-supplements-changed", handleSupplementChange);

    // Clean up
    return () => {
      window.removeEventListener("member-supplements-changed", handleSupplementChange);
    };
  }, [currentGroupMember?.id, queryClient, refetchSupplements]);

  // Update currentGroupMember whenever currentMember or currentGroup changes
  useEffect(() => {
    const updateGroupMember = async () => {
      await updateCurrentGroupMember(currentMember, userHomeData?.defaultGroup?.id, setCurrentGroupMember);
    };

    updateGroupMember();
  }, [currentMember, userHomeData, setCurrentGroupMember]);

  // Filter members based on search query
  const filteredMembers = useMemo(() => {
    return Array.isArray(members)
      ? members.filter((member) => member.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : [];
  }, [members, searchQuery]);

  // Style values
  const memberCardBg = useColorModeValue("gray.50", "gray.700");
  const memberCardBorder = useColorModeValue("gray.200", "gray.600");
  const memberNameColor = useColorModeValue("gray.800", "white");
  const memberInfoColor = useColorModeValue("gray.600", "gray.300");
  const editBadgeBg = useColorModeValue("blue.50", "blue.900");
  const editBadgeColor = useColorModeValue("blue.600", "blue.200");
  const groupTitleBg = useColorModeValue("green.50", "green.900");
  const groupTitleColor = useColorModeValue("green.700", "green.200");

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Reset search when menu closes
  const handleMenuClose = () => {
    setTimeout(() => setSearchQuery(""), 200);
  };

  // Handle member selection
  const handleMemberSelect = (member: Member) => {
    setCurrentMember(member);
  };

  return (
    <>
      <Meta title={pageTitle + (editMode && !isNewMember ? " (Editing)" : "")} />
      <Box bg={bgColor} minH="100vh" py={5}>
        <Container maxW="container.xl">
          {/* Group Title - Centered at top of page */}
          {userHomeData?.defaultGroup && (
            <Flex justify="center" mb={6}>
              <Heading
                as="h1"
                size="lg"
                textAlign="center"
                px={4}
                py={2}
                borderRadius="md"
                bg={groupTitleBg}
                color={groupTitleColor}
              >
                {userHomeData.defaultGroup.name}
              </Heading>
            </Flex>
          )}

          <Flex direction={{ base: "column", md: "row" }} justify="space-between" align="center" mb={6}>
            <Flex align="center">
              <Heading as="h1" size="xl" color={textColor}>
                {pageTitle}
              </Heading>
              {currentMember && editMode && !isNewMember && (
                <Badge ml={3} px={2} py={1} borderRadius="md" bg={editBadgeBg} color={editBadgeColor}>
                  Editing
                </Badge>
              )}
            </Flex>

            {currentMember ? (
              <HStack
                spacing={3}
                bg={memberCardBg}
                p={2}
                borderRadius="md"
                boxShadow="sm"
                borderWidth="1px"
                borderColor={memberCardBorder}
                width="320px"
              >
                <Avatar size="md" name={currentMember.name} src={currentMember.profileImageUrl} />
                <VStack align="start" spacing={0} flex="1" minWidth="0">
                  <Text fontWeight="bold" color={memberNameColor} noOfLines={1}>
                    {currentMember.name}
                  </Text>
                  <Text fontSize="sm" color={memberInfoColor} noOfLines={1}>
                    ID: {currentMember.id} • {currentMember.age} years •{" "}
                    {currentMember.gender === "M" ? "Male" : currentMember.gender === "F" ? "Female" : "Other"}
                  </Text>
                </VStack>
                <Menu onClose={handleMenuClose}>
                  <MenuButton as={Button} size="sm" rightIcon={<ChevronDownIcon />}>
                    Change
                  </MenuButton>
                  <MenuList>
                    <Box px={4} pb={2}>
                      <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none">
                          <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                          placeholder="Search members..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          autoFocus
                        />
                      </InputGroup>
                    </Box>
                    <MenuDivider />
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map((member) => (
                        <MenuItem key={member.id} onClick={() => handleMemberSelect(member)}>
                          <Flex align="center">
                            <Avatar size="xs" name={member.name} src={member.profileImageUrl} mr={2} />
                            <Text>{member.name}</Text>
                          </Flex>
                        </MenuItem>
                      ))
                    ) : (
                      <Box px={4} py={2}>
                        <Text fontSize="sm" color="gray.500">
                          No members found
                        </Text>
                      </Box>
                    )}
                  </MenuList>
                </Menu>
              </HStack>
            ) : (
              <Menu onClose={handleMenuClose}>
                <MenuButton as={Button} rightIcon={<ChevronDownIcon />} colorScheme="blue">
                  Select a Member
                </MenuButton>
                <MenuList>
                  <Box px={4} pb={2}>
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search members..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        autoFocus
                      />
                    </InputGroup>
                  </Box>
                  <MenuDivider />
                  {filteredMembers.length > 0 ? (
                    filteredMembers.map((member) => (
                      <MenuItem key={member.id} onClick={() => handleMemberSelect(member)}>
                        <Flex align="center">
                          <Avatar size="xs" name={member.name} src={member.profileImageUrl} mr={2} />
                          <Text>{member.name}</Text>
                        </Flex>
                      </MenuItem>
                    ))
                  ) : (
                    <Box px={4} py={2}>
                      <Text fontSize="sm" color="gray.500">
                        No members found
                      </Text>
                    </Box>
                  )}
                </MenuList>
              </Menu>
            )}
          </Flex>

          <Flex direction={{ base: "column", md: "row" }} alignItems="flex-start" gap={6}>
            {/* Member Card positioned on the left side */}
            <Box width={{ base: "75%", md: "340px" }} mx={0}>
              <MemberCard />
            </Box>

            {/* Current Supplements positioned on the right side - always shown when a member is selected */}
            {currentMember && currentMember.id > 0 && (
              <Box
                flex="1"
                width={{ base: "100%", md: "auto" }}
                minWidth={{ md: "400px" }}
                mt={{ base: 6, md: 0 }}
                position="relative"
              >
                {isLoadingSupplements ? (
                  <Flex justify="center" align="center" height="200px">
                    <Spinner size="xl" color="blue.500" />
                  </Flex>
                ) : (
                  <>
                    <MemberRecommendations
                      recommendations={memberSupplements?.recommendations}
                      currentSupplements={memberSupplements?.currentSupplements}
                    />
                    <CurrentSupplements supplements={memberSupplements?.currentSupplements} />
                    <RecentSupplements supplements={memberSupplements?.recentSupplements} />
                  </>
                )}
              </Box>
            )}
          </Flex>
        </Container>
      </Box>
    </>
  );
};

export const MembersPage = withRenderTally(MembersPageComponent, "MembersPage");
